import { useEffect, useState } from "react";
import { Users, TrendingUp, TrendingDown, Minus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

function fmt(v, d = 2) {
  if (v == null) return "—";
  return Number(v).toFixed(d);
}
function fmtPct(v) { return v == null ? "—" : `${Number(v).toFixed(2)}%`; }

function ResultChip({ result }) {
  const map = {
    WIN: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    LOSS: "text-red-300 border-red-500/40 bg-red-500/10",
    OPEN: "text-slate-300 border-slate-500/40 bg-slate-500/10",
    PENDING: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    NA: "text-slate-500 border-slate-700 bg-transparent",
  };
  return <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${map[result] || map.NA}`}>{result || "—"}</span>;
}

export default function SimilarSetups({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    if (!ticker) return;
    setLoading(true);
    api.get(`/signal/similar/${ticker}`, { params: { k: 30 } })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [ticker]);

  return (
    <div data-testid="similar-setups" className="border border-[#1E293B] bg-[#0F1626]">
      <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C] flex items-center gap-2">
        <Users className="h-3.5 w-3.5 text-cyan-400" />
        <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          Similar Historical Setups
        </span>
        <span className="mono text-[9px] uppercase tracking-widest text-slate-600 ml-auto">
          Backtested · in-sample
        </span>
      </div>

      {loading && (
        <div className="p-6 text-slate-500 mono text-xs text-center flex items-center justify-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching feature space…
        </div>
      )}

      {!loading && data && !data.found && (
        <div className="p-6 text-slate-500 mono text-xs text-center" data-testid="similar-not-found">
          {data.reason === "current_snapshot_is_wait"
            ? "Current signal is WAIT — no directional setup to match."
            : "No historical snapshots for this ticker yet. Run a backfill to populate."}
        </div>
      )}

      {!loading && data?.found && (
        <div className="p-3 space-y-3">
          {data.stats?.insufficient_history ? (
            <div className="mono text-[11px] text-amber-200 border border-amber-500/30 bg-amber-500/10 px-3 py-2" data-testid="similar-insufficient">
              Only {data.stats.sample_size} similar setups found — need at least {data.min_sample} for reliable statistics. Run a broader backfill to increase coverage.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <Stat label="Sample" value={data.stats.sample_size} testid="similar-sample" />
                <Stat label="Win Rate" value={fmtPct(data.stats.win_rate_pct)} tone={data.stats.win_rate_pct >= 55 ? "emerald" : data.stats.win_rate_pct >= 45 ? "slate" : "red"} testid="similar-win-rate" />
                <Stat label="Avg 5D" value={fmtPct(data.stats.avg_5d_return_pct)} tone={data.stats.avg_5d_return_pct >= 0 ? "emerald" : "red"} testid="similar-avg-5d" />
                <Stat label="Avg 20D" value={fmtPct(data.stats.avg_20d_return_pct)} tone={data.stats.avg_20d_return_pct >= 0 ? "emerald" : "red"} />
                <Stat label="Avg MAE" value={fmtPct(data.stats.avg_mae_pct)} tone="amber" testid="similar-avg-mae" />
              </div>
              <div className="pt-2 border-t border-[#151C2C]">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                  Nearest matches (top 8)
                </div>
                <table className="q-table">
                  <thead><tr><th>Ticker</th><th>As of</th><th className="right">Dist</th><th className="right">5D</th><th className="right">Result</th></tr></thead>
                  <tbody>
                    {(data.similar || []).slice(0, 8).map((s) => (
                      <tr key={s.snapshot_id}>
                        <td className="text-slate-100">{s.ticker}</td>
                        <td className="text-slate-400">{s.as_of_ts ? new Date(s.as_of_ts).toISOString().slice(0, 10) : "—"}</td>
                        <td className="right text-slate-400">{fmt(s.distance)}</td>
                        <td className={"right " + (s.return_5d > 0 ? "text-emerald-300" : s.return_5d < 0 ? "text-red-300" : "text-slate-500")}>
                          {s.return_5d != null ? (s.return_5d >= 0 ? "+" : "") + s.return_5d.toFixed(2) + "%" : "—"}
                        </td>
                        <td className="right"><ResultChip result={s.result} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "slate", testid }) {
  const cls = {
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
    red: "text-red-300 border-red-500/30 bg-red-500/5",
    amber: "text-amber-300 border-amber-500/30 bg-amber-500/5",
    slate: "text-slate-100 border-[#1E293B] bg-transparent",
  }[tone];
  return (
    <div data-testid={testid} className={`border ${cls} px-2.5 py-2`}>
      <div className="mono text-[9px] uppercase tracking-widest text-slate-500 leading-none">{label}</div>
      <div className="mono text-sm font-bold tabular-nums mt-1 leading-none">{value}</div>
    </div>
  );
}
